/**
 * 颜色值的小工具 —— 纯函数。
 *
 * 编辑器里颜色用**十六进制字符串**表示（`#rgb` / `#rrggbb` / `#rrggbbaa`），
 * 因为它无歧义、好存、和在模板 JSON 里也不会浮点漂移。
 * 运行时要 RGBA(0..1) 的话在这里转 —— 别让两套表示在中间层乱窜。
 */

const HEX_RE = /^#(?:[0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i

/** 是不是可接受的十六进制颜色 */
export function isHexColor(value: unknown): boolean {
  return typeof value === 'string' && HEX_RE.test(value.trim())
}

/** `#abc` → `#aabbcc`；已经是 6 位就原样返回 */
export function expandHex(value: string): string {
  const hex = value.trim()
  if (hex.length === 4) {
    return `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`.toLowerCase()
  }
  return hex.toLowerCase()
}

/**
 * 归一化成 `#rrggbb` —— 原生 `<input type="color">` 只吃这个格式。
 *
 * 8 位（带 alpha）会丢掉 alpha：原生取色器没有 alpha 通道，
 * 硬塞进去只会让它在打开时把 alpha 悄悄改掉。alpha 单独编辑，见下。
 */
export function toRgbHex(value: string): string {
  const hex = value.trim()
  if (hex.length === 9) return `#${hex.slice(1, 7)}`.toLowerCase()
  if (hex.length === 5) {
    const expanded = expandHex(hex.slice(0, 4))
    return expanded
  }
  if (hex.length === 4) return expandHex(hex)
  return hex.toLowerCase()
}

/** 取 alpha（0..1）；6 位 / 3 位视为不透明 */
export function alphaOf(value: string): number {
  const hex = value.trim()
  if (hex.length === 9) return Number.parseInt(hex.slice(7, 9), 16) / 255
  if (hex.length === 5) {
    const a = hex[4] ?? 'f'
    return Number.parseInt(a + a, 16) / 255
  }
  return 1
}

/**
 * 归一化：**满不透明就退回 6 位**。
 *
 * `#rrggbbff` 和 `#rrggbb` 等价，但前者会让模板 JSON 里多出无意义的两位，
 * 也让「有没有设过透明度」这件事看不出来。所以只在真的不完全透明时才写后缀。
 */
export function normalizeHex(value: string): string {
  const hex = value.trim().toLowerCase()
  if (hex.length === 5 && hex[4] === 'f') return expandHex(hex.slice(0, 4))
  if (hex.length === 9 && hex.slice(7, 9) === 'ff') return hex.slice(0, 7)
  // 3 位没带 alpha 的也展开成 6 位，统一格式
  if (hex.length === 4) return expandHex(hex)
  return hex
}

/**
 * 把颜色设成指定的 alpha（0..1）。
 *
 * alpha 满值时**不拼 `ff`** —— 直接返回 6 位。
 */
export function withAlpha(value: string, alpha: number): string {
  const rgb = toRgbHex(value)
  const clamped = Math.max(0, Math.min(1, alpha))
  const byte = Math.round(clamped * 255)
  // 0.999 这种四舍五入后也是 255 → 同样当作不透明
  if (byte >= 255) return rgb
  return `${rgb}${byte.toString(16).padStart(2, '0')}`
}

/** 转成运行时用的 RGBA(0..1)，给求值层 / C++ 侧用 */
export function toRgbaUnit(value: string): {
  r: number
  g: number
  b: number
  a: number
} | null {
  if (!isHexColor(value)) return null
  const rgb = toRgbHex(value)
  return {
    r: Number.parseInt(rgb.slice(1, 3), 16) / 255,
    g: Number.parseInt(rgb.slice(3, 5), 16) / 255,
    b: Number.parseInt(rgb.slice(5, 7), 16) / 255,
    a: alphaOf(value),
  }
}

/* ══════════════════════════════════════════════════════════════
   HSV —— 拾色面板要用的色彩空间
   ══════════════════════════════════════════════════════════════

   拾色面板的「饱和度 / 明度方块 + 色相条」本质上是在 HSV 里取值，
   而存的是十六进制。所以两边都要有。

   ⚠️ 灰色（s = 0）的色相在数学上**是未定义的** ——
    只能返回 0。面板必须自己记住用户选的色调，
   否则把饱和度拖到 0 再拖回来，颜色会跳到红色。
*/

export interface Hsv {
  /** 0..360 */
  h: number
  /** 0..1 */
  s: number
  /** 0..1 */
  v: number
}

function clamp01(n: number): number {
  return Math.max(0, Math.min(1, n))
}

/** 十六进制 → 0..255 的 RGB */
export function hexToRgb255(
  value: string,
): { r: number; g: number; b: number } | null {
  if (!isHexColor(value)) return null
  const rgb = toRgbHex(value)
  return {
    r: Number.parseInt(rgb.slice(1, 3), 16),
    g: Number.parseInt(rgb.slice(3, 5), 16),
    b: Number.parseInt(rgb.slice(5, 7), 16),
  }
}

/** 0..255 的 RGB → 十六进制（6 位，不带 alpha） */
export function rgb255ToHex(r: number, g: number, b: number): string {
  const byte = (n: number) =>
    Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0')
  return `#${byte(r)}${byte(g)}${byte(b)}`
}

export function rgbToHsv(r: number, g: number, b: number): Hsv {
  const rn = r / 255
  const gn = g / 255
  const bn = b / 255
  const max = Math.max(rn, gn, bn)
  const min = Math.min(rn, gn, bn)
  const d = max - min

  let h = 0
  if (d !== 0) {
    if (max === rn) h = ((gn - bn) / d) % 6
    else if (max === gn) h = (bn - rn) / d + 2
    else h = (rn - gn) / d + 4
    h *= 60
    if (h < 0) h += 360
  }

  return { h, s: max === 0 ? 0 : d / max, v: max }
}

export function hsvToRgb(h: number, s: number, v: number): { r: number; g: number; b: number } {
  const sat = clamp01(s)
  const val = clamp01(v)
  const c = val * sat
  const hh = ((((h % 360) + 360) % 360)) / 60
  const x = c * (1 - Math.abs((hh % 2) - 1))
  const m = val - c

  let rgb: [number, number, number]
  if (hh < 1) rgb = [c, x, 0]
  else if (hh < 2) rgb = [x, c, 0]
  else if (hh < 3) rgb = [0, c, x]
  else if (hh < 4) rgb = [0, x, c]
  else if (hh < 5) rgb = [x, 0, c]
  else rgb = [c, 0, x]

  return {
    r: Math.round((rgb[0] + m) * 255),
    g: Math.round((rgb[1] + m) * 255),
    b: Math.round((rgb[2] + m) * 255),
  }
}

export function hexToHsv(value: string): Hsv | null {
  const rgb = hexToRgb255(value)
  if (!rgb) return null
  return rgbToHsv(rgb.r, rgb.g, rgb.b)
}

/** HSV + alpha → 十六进制；alpha 满值时不拼 ff（见 withAlpha） */
export function hsvToHex(hsv: Hsv, alpha = 1): string {
  const { r, g, b } = hsvToRgb(hsv.h, hsv.s, hsv.v)
  return withAlpha(rgb255ToHex(r, g, b), alpha)
}
